import { $, $$, sliderConfig } from '@utils';
import Swiper from 'swiper';
import { Navigation, Scrollbar, FreeMode, A11y } from 'swiper/modules';

function init() {
  if (!$('[data-au-figures]')) return;

  initDoorIconLoop();
  initCypherCounterOnScroll();
  initFigureSliders();

  if (document.body.dataset.auFiguresSliderBound !== 'true') {
    window.addEventListener('resize', initFigureSliders);
    document.body.dataset.auFiguresSliderBound = 'true';
  }
}

function initFigureSliders() {
  $$('[data-au-figures-slider]').forEach((sliderEl) => {
    if (shouldCreateFigureSwiper(sliderEl)) {
      createFigureSwiper(sliderEl);
      return;
    }

    destroyFigureSwiper(sliderEl);
  });
}

function shouldCreateFigureSwiper(sliderEl) {
  const wrapperEl = sliderEl.querySelector('.swiper-wrapper');

  if (!wrapperEl) return false;

  return wrapperEl.scrollWidth - sliderEl.clientWidth > 1;
}

function createFigureSwiper(sliderEl) {
  if (sliderEl._swiper) return;

  const swiper = new Swiper(sliderEl, {
    modules: [Navigation, Scrollbar, FreeMode, A11y],
    ...sliderConfig,
    spaceBetween: 0,
    freeMode: true,
    slidesPerView: 'auto',
    on: {
      ...sliderConfig.on,
      init() {
        toggleControls(this);
      },
      update() {
        toggleControls(this);
      },
      resize() {
        toggleControls(this);
      },
    },
  });

  toggleControls(swiper);
  sliderEl._swiper = swiper;
}

function destroyFigureSwiper(sliderEl) {
  if (!sliderEl._swiper) {
    toggleSliderControls(sliderEl, true);
    return;
  }

  sliderEl._swiper.destroy(true, true);
  sliderEl._swiper = null;
  sliderEl.querySelectorAll('.swiper-slide[inert]').forEach((slide) => {
    slide.removeAttribute('inert');
  });
  toggleSliderControls(sliderEl, true);
}

function toggleControls(swiper) {
  toggleSliderControls(swiper.el, swiper.isLocked);
}

function toggleSliderControls(sliderEl, isHidden) {
  const controls = sliderEl.querySelector('[data-au-slider-controls]');
  if (!controls) return;

  controls.classList.toggle('au-hide', isHidden);
}

function initDoorIconLoop() {
  const iconShowDuration = 2000;
  const iconAnimationDuration = 500;
  const iconSwapInterval = iconShowDuration + iconAnimationDuration;

  $$('[data-au-figures-door][data-au-figures-door-icon]').forEach((doorEl) => {
    if (doorEl.dataset.auFiguresDoorLoopReady === 'true') return;

    const iconEl = doorEl.querySelector('.material-symbols-rounded[data-au-icon]');
    if (!iconEl) return;

    const firstIcon = iconEl.textContent.trim();
    const secondIcon = (doorEl.dataset.auFiguresDoorIcon || '').trim();

    if (!secondIcon || secondIcon === firstIcon) return;

    doorEl.dataset.auFiguresDoorLoopReady = 'true';

    let showingFirst = true;
    window.setInterval(() => {
      iconEl.classList.add('au-figures__door-icon--fading');

      window.setTimeout(() => {
        iconEl.textContent = showingFirst ? secondIcon : firstIcon;
        showingFirst = !showingFirst;
        iconEl.classList.remove('au-figures__door-icon--fading');
      }, iconAnimationDuration);
    }, iconSwapInterval);
  });
}

function initCypherCounterOnScroll() {
  const cypherEls = $$('[data-au-figures] [data-au-figure-card-cypher]');
  if (!cypherEls.length) return;

  const observer = new IntersectionObserver(
    (entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        animateCypher(entry.target);
        currentObserver.unobserve(entry.target);
      });
    },
    {
      threshold: 0.45,
    },
  );

  cypherEls.forEach((cypherEl) => {
    if (cypherEl.dataset.auFigureCypherAnimated === 'true') return;
    observer.observe(cypherEl);
  });
}

function animateCypher(cypherEl) {
  const originalText = cypherEl.textContent.trim();
  const descriptor = getCypherDescriptor(originalText);
  if (!descriptor) return;

  if (descriptor.absValue === 0) {
    cypherEl.dataset.auFigureCypherAnimated = 'true';
    return;
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    cypherEl.textContent = originalText;
    cypherEl.dataset.auFigureCypherAnimated = 'true';
    return;
  }

  const duration = 3000;
  const start = performance.now();
  cypherEl.classList.add('au-figure-card__cypher--animating');

  function step(now) {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = 1 - Math.pow(1 - progress, 3);
    let currentAbsValue;

    if (descriptor.absValue >= 1000) {
      const stepValue = Math.max(4, Math.round(descriptor.absValue / 250));
      currentAbsValue = Math.round((descriptor.absValue * easedProgress) / stepValue) * stepValue;
    } else {
      currentAbsValue = descriptor.absValue * easedProgress;
    }

    const valueText = formatCypherValue(currentAbsValue, descriptor);

    cypherEl.textContent = `${descriptor.prefix}${valueText}${descriptor.suffix}`;

    if (progress < 1) {
      window.requestAnimationFrame(step);
      return;
    }

    cypherEl.textContent = originalText;
    cypherEl.dataset.auFigureCypherAnimated = 'true';
    cypherEl.classList.remove('au-figure-card__cypher--animating');
  }

  window.requestAnimationFrame(step);
}

function getCypherDescriptor(value) {
  const numericMatch = value.match(/\d[\d.,]*/);
  if (!numericMatch) return null;

  const token = numericMatch[0];
  const tokenIndex = numericMatch.index;
  const prefix = value.slice(0, tokenIndex);
  const suffix = value.slice(tokenIndex + token.length);
  const normalized = token.replace(/\./g, '').replace(',', '.');
  const absValue = Number.parseFloat(normalized);
  if (Number.isNaN(absValue)) return null;

  return {
    token,
    prefix,
    suffix,
    absValue,
    decimals: token.includes(',') ? token.split(',')[1].length : 0,
    hasThousandsSeparator: token.includes('.'),
    hasLeadingZeros: /^0\d+$/.test(token),
  };
}

function formatCypherValue(value, descriptor) {
  const roundedValue = descriptor.decimals > 0 ? Number(value.toFixed(descriptor.decimals)) : Math.round(value);

  if (descriptor.hasLeadingZeros && descriptor.decimals === 0) {
    return String(roundedValue).padStart(descriptor.token.length, '0');
  }

  if (!descriptor.hasThousandsSeparator && descriptor.decimals === 0) {
    return String(roundedValue);
  }

  return roundedValue.toLocaleString('es-ES', {
    minimumFractionDigits: descriptor.decimals,
    maximumFractionDigits: descriptor.decimals,
  });
}

export default { init };
